// HUB-UI handoff: contracts for Hub design 05/06 and PlatformDB 040–044.
// Organization profile contracts are served by OrganizationProfileEndpoints in PlatformAPI.
import { http } from '../../core/http/client';
import type { OrganizationCard, Paged } from '../public/publicApi';

export type Values = Record<string, string | number | boolean | null>;
export interface Address extends Values {
  id: string;
  recipientName: string;
  phone: string;
  addressLine: string;
  city: string;
  countryCode: string;
  isDefault: boolean;
}
export interface Preferences extends Values {
  language: string;
  emailEnabled: boolean;
  smsEnabled: boolean;
  remindersEnabled: boolean;
  orderUpdatesEnabled: boolean;
  applicationUpdatesEnabled: boolean;
  marketingEnabled: boolean;
  defaultOrganizationId: string | null;
  defaultDestinationCode: string | null;
}
export interface CustomerRecord {
  id: string;
  title: string;
  subtitle: string | null;
  recordType: string;
  productCode: string;
  organizationName: string;
  status: string;
  statusCategory: string;
  occursAt: string;
  amount: number | null;
  currencyCode: string | null;
  detailPath: string | null;
  publicSiteUrl: string | null;
}
export interface Overview {
  nextReservation: CustomerRecord | null;
  activeOrders: CustomerRecord[];
  applications: number;
  favourites: number;
}
export interface Timeline {
  months: { month: number; records: CustomerRecord[] }[];
  totals: { currencyCode: string; amount: number }[];
}
export interface Application {
  id: string;
  jobId: string;
  title: string;
  organizationName: string;
  stage: string;
  submittedAt: string;
}
/** A row of the organization profiles grid: one business of the organization, for one product. */
export interface OrganizationProfileSummary {
  id: string;
  productCode: string;
  productName: string;
  productIcon: string | null;
  displayName: string | null;
  slug: string | null;
  city: string | null;
  isPublic: boolean;
  listedAt: string | null;
  updatedAt: string;
  checklistComplete: boolean;
}
export interface OrganizationProfileList {
  items: OrganizationProfileSummary[];
  /** Enabled products that have no profile yet: what "Add profile" can create. */
  availableProducts: { code: string; displayName: string; icon: string | null }[];
}
/**
 * A profile or cover photo served by the shared Media Server. A legacy photo (not migrated yet)
 * has no mediaId and the same PlatformAPI URL in every slot; it needs the signed-in fetch.
 */
export interface ProfilePhoto {
  mediaId: string | null;
  lqipUrl: string | null;
  thumbUrl: string;
  cardUrl: string;
  fullUrl: string;
  srcset: string | null;
  width: number | null;
  height: number | null;
  dominantColor: string | null;
}
/** The photo objects of a profile response (kept apart because Values only holds plain fields). */
export interface OrganizationProfilePhotos {
  logo?: ProfilePhoto | null;
  cover?: ProfilePhoto | null;
}
/** Everything a profile edits. Mirrors OrganizationProfileRequest in PlatformAPI. */
export interface OrganizationProfile extends Values {
  orderConfirmationEmailEnabled: boolean;
  logoUrl: string | null;
  coverUrl: string | null;
  id: string;
  productCode: string;
  productName: string;
  productIcon: string | null;
  registrationNumber: string | null;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  slug: string | null;
  customUrl: string | null;
  addressLine: string | null;
  city: string | null;
  postalCode: string | null;
  countryCode: string | null;
  latitude: number | null;
  longitude: number | null;
  instagramUrl: string | null;
  facebookUrl: string | null;
  tikTokUrl: string | null;
  workingHours: string | null;
  isPublic: boolean;
  listedAt: string | null;
  isVerified: boolean;
  timeZone: string;
  currencyCode: string;
  isVatRegistered: boolean;
  defaultVatRate: number | null;
  bankTransactionPercentage: number | null;
  menuKeyCode: string | null;
  integrationId: number | null;
  licenseKey: string | null;
  licenseUrl: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface CompanyText {
  languageCode: string;
  tagline: string | null;
  about: string | null;
}
export interface Category extends Values {
  code: string;
  parentCode: string | null;
  nameAl: string;
  nameEn: string;
  nameIt: string;
  icon: string;
  sortOrder: number;
  isActive: boolean;
}
export interface Listing {
  productCode: string;
  isPublished: boolean;
  publicPath: string;
  minPrice: number | null;
  currencyCode: string | null;
  hasOnlineBooking: boolean;
  hasShop: boolean;
  itemCount: number;
  updatedAt: string;
}
export interface Checklist {
  items: { code: string; label: string; complete: boolean }[];
  canList: boolean;
}
export interface Job extends Values {
  id: string;
  titleAl: string | null;
  titleEn: string | null;
  titleIt: string | null;
  roleCode: string;
  city: string;
  employmentType: string;
  status: string;
  rowVersion: string;
}
export interface Applicant extends Values {
  id: string;
  name: string;
  email: string;
  stage: string;
  rating: number | null;
  submittedAt: string;
}

export async function read<T>(path: string, params?: object, signal?: AbortSignal): Promise<T> {
  return (await http().get<T>(path, { params, signal, skipErrorToast: true })).data;
}
export async function write<T = void>(
  path: string,
  body: unknown,
  method: 'post' | 'put' | 'delete' = 'put',
): Promise<T> {
  return (await http().request<T>({ url: path, method, data: body, skipErrorToast: true })).data;
}
export const hubApi = {
  overview: (signal?: AbortSignal) => read<Overview>('/me/overview', undefined, signal),
  records: (params: object, signal?: AbortSignal) => read<Paged<CustomerRecord>>('/me/records', params, signal),
  timeline: (year: number, signal?: AbortSignal) => read<Timeline>('/me/records/timeline', { year }, signal),
  favourites: (signal?: AbortSignal) => read<OrganizationCard[]>('/me/favourites', undefined, signal),
  applications: (signal?: AbortSignal) => read<Application[]>('/me/applications', undefined, signal),
  addresses: (signal?: AbortSignal) => read<Address[]>('/me/addresses', undefined, signal),
  preferences: (signal?: AbortSignal) => read<Preferences>('/me/preferences', undefined, signal),
  profiles: (signal?: AbortSignal) => read<OrganizationProfileList>('/organization-profiles', undefined, signal),
  profile: (id: string, signal?: AbortSignal) =>
    read<OrganizationProfile & OrganizationProfilePhotos>(`/organization-profiles/${encodeURIComponent(id)}`, undefined, signal),
  checklist: (id: string, signal?: AbortSignal) =>
    read<Checklist>(`/organization-profiles/${encodeURIComponent(id)}/checklist`, undefined, signal),
  listings: (signal?: AbortSignal) => read<Listing[]>('/organization-profiles/listings', undefined, signal),
};

// Detail destinations are product-owned. Reject absolute paths and schemes supplied as DetailPath.
export function recordUrl(record: CustomerRecord): string | null {
  if (
    !record.publicSiteUrl ||
    !record.detailPath?.startsWith('/') ||
    record.detailPath.startsWith('//') ||
    /[\\\u0000-\u0020]/.test(record.detailPath)
  )
    return null;
  try {
    const base = new URL(record.publicSiteUrl);
    if (!['http:', 'https:'].includes(base.protocol) || base.username || base.password) return null;
    const target = new URL(record.detailPath, base);
    return target.origin === base.origin ? target.href : null;
  } catch {
    return null;
  }
}
